import { useEffect } from 'react';

export interface ZzzfunSeoOptions {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
}

function upsertMeta(
  attr: 'name' | 'property',
  key: string,
  content?: string,
): void {
  const selector = `meta[${attr}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (content === undefined) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    existing.setAttribute('content', content);
    return;
  }
  const meta = document.createElement('meta');
  meta.setAttribute(attr, key);
  meta.setAttribute('content', content);
  document.head.appendChild(meta);
}

function upsertCanonical(href?: string): void {
  const existing = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (href === undefined) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    existing.setAttribute('href', href);
    return;
  }
  const link = document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', href);
  document.head.appendChild(link);
}

/**
 * Keeps document title and SEO meta tags in sync with the active page.
 * Because this is a single-page app, each route applies its own head values
 * on mount so crawlers and browsers see the correct metadata.
 */
export function useZzzfunSeo(options: ZzzfunSeoOptions): void {
  const { title, description, keywords, canonical } = options;

  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertCanonical(canonical);
  }, [title, description, keywords, canonical]);
}